import {
  addDays,
  differenceInCalendarDays,
  differenceInMonths,
  differenceInYears,
  format,
  isValid,
  isWeekend,
} from 'date-fns';
import DateHolidays from 'date-holidays';
import { matchSorter } from 'match-sorter';
import { type Config, type Router } from 'ziggy-js';
import { isString } from 'lodash';
import { captureException } from '@sentry/browser';

import { File, FileUploadedByKind, Firm, NoteData, Team, User } from '@/types';
import { FILE_DELETE_BLOCKED_REASONS, Permission } from '@/core/const';
import { SUPER_ADMIN_ROLE, SYSTEM_ADMIN_ROLE_TYPE } from '@/pages/roles/const';

const holidays = new DateHolidays('US');

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export const formatCurrencyUSD = (amount: number): string => {
  return usdFormatter.format(amount);
};

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function truncateString(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 3) + '...';
}

export const isSuperAdmin = (roles: string[] | undefined): boolean => {
  return Array.isArray(roles) && roles.includes(SUPER_ADMIN_ROLE);
};

export const isSystemAdmin = (roleType: string): boolean => {
  return roleType === SYSTEM_ADMIN_ROLE_TYPE;
};

/**
 * The fields can() reads, so partial payloads can be checked too. `pid` is
 * carried along purely as a discriminator: `permissions` and `roles` are both
 * optional on `User`, so without it this is a weak type that any object with a
 * `permissions: string[]` satisfies — `RoleFormValues` included, which would
 * make `can()` silently answer about a role definition instead of the viewer.
 */
export type PermissionSubject = Pick<User, 'pid' | 'permissions' | 'roles'>;

export const can = (
  user: PermissionSubject,
  permission: Permission,
): boolean => {
  return !!user.permissions?.includes(permission) || isSuperAdmin(user.roles);
};

// Can apply/change/remove tags on clients and workflows — mirrors the
// taxpayer-edit gate on the backend (HWEPD-10020).
export const canManageClientTags = (user: User): boolean =>
  can(user, Permission.MANAGE_TAXPAYERS) ||
  can(user, Permission.TEAM_MANAGE_TAXPAYERS);

// Can manage the firm's tag vocabulary in settings — also gates the
// "Manage tags in Firm Settings" deep-link in the tag pickers (HWEPD-10020).
export const canManageTagVocabulary = (user: User): boolean =>
  can(user, Permission.MANAGE_TEAMS) || can(user, Permission.TEAM_MANAGE_TEAMS);

export const canManageJobRoles = (
  user: User,
  firm: Firm | null | undefined,
): boolean =>
  (firm?.isAdvisoryFirm ?? false) &&
  (can(user, Permission.MANAGE_TEAMS) ||
    can(user, Permission.TEAM_MANAGE_TEAMS));

// Compared upper-cased, as every reader of this column on the backend does.
const TEAM_DELETABLE_FILE_KINDS: string[] = [
  FileUploadedByKind.harnessTax,
  FileUploadedByKind.client,
];

/**
 * Whether the grid offers Delete on a Socrates file. Mirrors the tiers in
 * `SocratesFileDeleteRights`, which additionally checks client access.
 */
export const canDeleteSocratesFile = (
  user: PermissionSubject,
  file:
    | Pick<File, 'uploadedByKind' | 'aeUserPid' | 'aeTaxpayerPid'>
    | null
    | undefined,
): boolean => {
  if (!file) {
    return false;
  }

  const isOwnUpload = !!file.aeUserPid && file.aeUserPid === user.pid;

  // A contact-level upload has no client to check tenancy against, so the
  // endpoint allows only its uploader, whatever tier the viewer holds.
  if (!file.aeTaxpayerPid) {
    return isOwnUpload;
  }

  return (
    can(user, Permission.DELETE_FILES) ||
    (can(user, Permission.TEAM_DELETE_FILES) &&
      TEAM_DELETABLE_FILE_KINDS.includes(
        String(file.uploadedByKind ?? '').toUpperCase(),
      )) ||
    isOwnUpload
  );
};

/**
 * Why Delete is disabled on a file, or null when it can be deleted. A
 * contact-level file is uploader-only, as in `canDeleteSocratesFile`.
 */
export const fileDeleteBlockedReason = (
  canDelete: boolean,
  file: { isInternal: boolean; isContactLevel?: boolean },
): string | null => {
  if (canDelete) {
    return null;
  }

  if (file.isInternal) {
    return FILE_DELETE_BLOCKED_REASONS.attached;
  }

  if (file.isContactLevel) {
    return FILE_DELETE_BLOCKED_REASONS.uploaderOnly;
  }

  return FILE_DELETE_BLOCKED_REASONS.noPermission;
};

// styled utility we're using does not support transientProps so need this wrapper to prevent errors in console
//github.com/emotion-js/emotion/issues/2193
export const withTransientProps = {
  shouldForwardProp: (propName: string): boolean =>
    propName !== 'theme' && !propName.startsWith('$'),
};

export const toLocaleTimezone = (date: Date): string => {
  return date.toLocaleString('en-US', {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
};

export const utcDateToLocal = (date: Date): Date => {
  return new Date(toLocaleTimezone(date));
};

export const timestampToRelativeTimeFromNow = (timestamp: Date): string => {
  const now = new Date();
  const daysDiff = differenceInCalendarDays(now, timestamp);
  const monthsDiff = differenceInMonths(now, timestamp);
  const yearsDiff = differenceInYears(now, timestamp);

  if (daysDiff === 0) {
    return 'Today';
  }
  if (daysDiff === 1) {
    return 'Yesterday';
  }
  if (daysDiff < 30) {
    return `${daysDiff} days ago`;
  }
  if (monthsDiff === 1) {
    return 'Last month';
  }
  if (monthsDiff < 12) {
    return `${monthsDiff} months ago`;
  }
  if (yearsDiff === 1) {
    return 'Last year';
  }
  return `${yearsDiff} years ago`;
};
// 1/31/24, 4:53 PM
export const timestampToShortDateTime = (date: Date): string => {
  return format(utcDateToLocal(date), 'M/d/yy, h:mm a');
};

// 1/31, 4:53 PM
export const timestampToDateMonthTime = (date: Date): string => {
  if (!isValid(date)) {
    return '';
  }
  return format(utcDateToLocal(date), 'M/d, h:mm a');
};

// 1/31/24,
export const timestampToShortDate = (date: Date): string => {
  if (!isValid(date)) {
    return '';
  }
  return format(utcDateToLocal(date), 'M/d/yy');
};

// July 1, 2025
export const timestampToFullDate = (date: Date): string => {
  if (!isValid(date)) {
    return '';
  }
  return format(utcDateToLocal(date), 'MMMM d, yyyy');
};

export const fuzzySearchMultipleWords = <T>(
  rows: T[],
  keys: string[],
  filterValue: string,
): T[] => {
  if (!filterValue || !filterValue.length) {
    return rows;
  }

  const terms = filterValue.split(' ');
  if (!terms) {
    return rows;
  }

  return terms.reduceRight(
    (results, term) => matchSorter(results, term, { keys }),
    rows,
  );
};

export const getDefaultTaxYear = (): number => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;
  const day = currentDate.getDate();

  if ((month === 10 && day >= 15) || month > 10) {
    return currentYear;
  }

  return currentYear - 1;
};

export const getNextBusinessDay = (date: Date): Date => {
  const isHoliday = !!holidays.isHoliday(date);

  if (!isWeekend(date) && !isHoliday) {
    return date;
  }

  const nextDate = addDays(date, 1);
  return getNextBusinessDay(nextDate);
};

export function firmRoute(
  name: string,
  params: Record<string, any> = {},
): string {
  let firmPid = '';
  type ZiggyRoute = Router & {
    t: Config;
  };
  const routeDetails = route() as ZiggyRoute;
  if (routeDetails.params.firm) {
    firmPid = routeDetails.params.firm;
  } else if (routeDetails.t.defaults.firm) {
    // Ziggy sometimes returns a Firm object, sometimes a string
    // TODO: figure out why this is happening
    const firm = routeDetails.t.defaults.firm as Firm | string;
    firmPid = isString(firm) ? firm : firm.pid;
  } else {
    firmPid = JSON.parse(localStorage.getItem('firm_pid') as string).firmPid;
  }

  return route(name, { firm: firmPid, ...params });
}

export type TeamRouteAction =
  | 'show'
  | 'edit'
  | 'update'
  | 'edit-members'
  | 'update-members-and-invites'
  | 'add-members'
  | 'remove-member'
  | 'edit-taxpayers'
  | 'update-taxpayers'
  | 'add-taxpayer'
  | 'remove-taxpayers'
  | 'edit-contacts'
  | 'update-contacts'
  | 'add-contact'
  | 'remove-contact';

const ADMIN_TEAM_RESOURCE_ACTIONS: TeamRouteAction[] = [
  'show',
  'edit',
  'update',
];

/**
 * The fields teamRoute() actually reads. Kept narrower than `Team` so callers
 * holding a partial payload — e.g. the `{ pid, name }` items AssociatedResourcesList
 * yields — can use the helper instead of hand-binding the route.
 *
 * Only safe for the 'admin' scope. `Team.firm` is required, so a full `Team` was
 * previously the compiler's guarantee that the 'firm' scope had a firm to bind;
 * a partial reaching that scope instead falls through to firmRoute() and binds
 * the *ambient* firm to this team's pid — a cross-firm URL on any admin list.
 */
export type TeamRouteTarget = Pick<Team, 'pid'> & {
  firm?: Pick<Firm, 'pid'> | null;
};

/**
 * URL for a team action, honoring the route scope: 'firm' targets the
 * firm-scoped `firms.teams.*` routes, 'admin' the legacy `/admin/teams/*`
 * routes (`teams.*` resource names for show/edit/update, `team.*` for verb
 * routes). Falls back to firmRoute() when the team payload has no firm
 * relation loaded.
 */
export function teamRoute(
  scope: 'admin' | 'firm',
  action: TeamRouteAction,
  team: TeamRouteTarget,
  params: Record<string, unknown> = {},
): string {
  if (scope === 'firm') {
    if (team.firm?.pid) {
      return route(`firms.teams.${action}`, {
        firm: team.firm.pid,
        team: team.pid,
        ...params,
      });
    }

    return firmRoute(`firms.teams.${action}`, { team: team.pid, ...params });
  }

  const routeName = ADMIN_TEAM_RESOURCE_ACTIONS.includes(action)
    ? `teams.${action}`
    : `team.${action}`;

  return route(routeName, { team: team.pid, ...params });
}

/**
 * URL for a contact detail page reached from a cross-firm admin list.
 *
 * MANAGE_CONTACTS holders get the admin page, which carries no firm scope and
 * is the cross-firm identity view. Viewers without it would be 403'd there by
 * Admin\ContactController, and the Contact firm global scope only lifts for
 * MANAGE_CONTACTS holders, so the contacts they can see on such a list are
 * their own firm's and firmRoute() resolves that firm for them. The firm-scoped
 * page still admits them only for contacts they share a team with, so some of
 * these links 403 — unchanged from before the admin route existed.
 *
 * Call it from a click handler rather than during render: every call is a Ziggy
 * template build, and the grids that use it are unvirtualized.
 */
export const contactDetailRoute = (
  user: PermissionSubject,
  contactPid: string,
): string =>
  can(user, Permission.MANAGE_CONTACTS)
    ? route('admin.contacts.show', { contact: contactPid })
    : firmRoute('contacts.show', { contact: contactPid });

export const getTaskChipLabel = (numberOfTasks: number): string => {
  if (numberOfTasks === 0) return '— task';
  return `${numberOfTasks} task${numberOfTasks > 1 ? 's' : ''}`;
};

/**
 * Formats a count with its noun, appending an "s" to the noun unless the count
 * is exactly one, e.g. "1 member" or "3 members".
 */
export const pluralize = (count: number, noun: string): string =>
  `${count} ${count === 1 ? noun : `${noun}s`}`;

export const getClientPreviewLabel = (
  quarter: number | null,
  month: number | null,
  year: number | null,
  templateName: string | null,
  clintFacingName: string | null,
): string => {
  const name = clintFacingName || templateName;
  if (!name) return '—';

  if (!year) return name;

  if (quarter && year) return `${year} Q${quarter} ${name}`;

  if (month && year) return `${year}-${String(month).padStart(2, '0')} ${name}`;

  return `${year} ${name}`;
};

/**
 * Up to two initials for an avatar: the first and last meaningful words of the
 * name. Words without a letter or digit (a separator like the em dash in
 * "Upsell Demo — High Fit") never become an initial, and the result is capped
 * at two characters — a 24px avatar wraps and clips anything longer.
 */
export const getInitials = (name?: string | null): string => {
  if (!name) {
    return '—';
  }

  const words = name.split(/\s+/).filter((word) => /[\p{L}\p{N}]/u.test(word));

  if (words.length === 0) {
    return '—';
  }

  const firstLetter = (word: string): string =>
    word.match(/[\p{L}\p{N}]/u)![0].toUpperCase();

  const initials =
    words.length === 1
      ? firstLetter(words[0])
      : firstLetter(words[0]) + firstLetter(words[words.length - 1]);

  return initials;
};

export const getPreviewForText = (
  taxpayerName?: string,
  advisorName?: string | null,
): string => {
  if (!taxpayerName && advisorName) return `for — with ${advisorName}`;

  if (taxpayerName && advisorName)
    return `for ${taxpayerName} with ${advisorName}`;

  return 'for — with —';
};

export const parseDateFromString = (
  dateStr: string | null | undefined,
): Date | null => {
  if (!dateStr) return null;

  try {
    const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
    const parsed = new Date(year, month - 1, day);
    // A non-date string yields an Invalid Date, which is truthy — so every
    // caller's `if (!parsed)` guard would let it through and date-fns `format()`
    // would throw. URL params reach here verbatim (?dueDateFrom=lol), so the
    // null contract has to be honored rather than assumed.
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch (error) {
    captureException(error);
    return null;
  }
};

export const randomString = (): string => {
  return crypto.randomUUID();
};

export const normalizeUrl = (url: string): string => {
  const trimmed = url.trim();
  if (trimmed && !/^https?:\/\//i.test(trimmed)) {
    return `https://${trimmed}`;
  }
  return trimmed;
};

export const isValidUrl = (url: string): boolean => {
  try {
    const parsed = new URL(normalizeUrl(url));
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

export const formatLinkDisplayUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    const domain = parsed.hostname.replace(/^www\./, '');
    return domain + (parsed.pathname !== '/' ? parsed.pathname : '');
  } catch {
    return url;
  }
};

/** Phone numbers are stored in E.164; US ones read better nationally. */
export const formatPhoneNumber = (phone: string): string => {
  const usNumber = phone.match(/^\+1(\d{3})(\d{3})(\d{4})$/);

  if (!usNumber) {
    return phone;
  }

  const [, areaCode, exchange, lineNumber] = usNumber;

  return `(${areaCode}) ${exchange}-${lineNumber}`;
};

export const formatDueDate = (dueDate: string | null | undefined): string => {
  const parsedDate = parseDateFromString(dueDate);
  return parsedDate ? format(parsedDate, 'MM/dd/yy') : '';
};

export const dateOnlyToShortDate = (
  dateStr: string | null | undefined,
): string => {
  const parsed = parseDateFromString(dateStr);
  if (!parsed) return '';
  return format(parsed, 'M/d/yy');
};

/**
 * Order notes the same way the backend `Note::scopeChronological` does:
 * newest note date first, then newest created timestamp as a tie-breaker.
 * `noteDate` (`yyyy-MM-dd`) and `createdAt` (ISO 8601) are both lexically
 * sortable, so string comparison is chronological. Used to keep optimistic
 * client-side updates consistent with the server's ordering.
 */
/**
 * Whether a saved note is still filed under the client whose page is open
 * (HWEPD-10164). A note edit can untick that very client from "Applies to", and
 * then the note has to leave that client's list. `appliesTo` is absent whenever
 * the caller did not load the shares — treat that as "still applies" rather
 * than dropping the row on missing data.
 */
export const noteAppliesToTaxpayer = (
  note: NoteData,
  taxpayerPid: string,
): boolean =>
  note.appliesTo === undefined ||
  note.appliesTo.some((entry) => entry.pid === taxpayerPid);

export const sortNotesChronologically = (notes: NoteData[]): NoteData[] =>
  [...notes].sort((a, b) => {
    if (a.noteDate !== b.noteDate) {
      return a.noteDate < b.noteDate ? 1 : -1;
    }
    if (a.createdAt !== b.createdAt) {
      return a.createdAt < b.createdAt ? 1 : -1;
    }
    return 0;
  });
